import { Button, Checkbox, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Skeleton, Spinner } from '@1trading/react';
import { Download } from 'lucide-react';
import { useEffect, useState } from 'react';
import { DEFAULT_DOWNLOAD_STATUSES, getTermsVersionNote, USER_STATUS_VALUES, type UserStatusValue } from '@/services/api/terms';
import { capitalize } from '@/utils/string';

export interface TermsExportSelection {
  statuses: UserStatusValue[];
  versions: string[];
}

interface TermsDownloadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (selection: TermsExportSelection) => void;
  isDownloading: boolean;
  versions: string[];
  isVersionsLoading: boolean;
  versionsError: string | null;
  onRetryVersions: () => void;
}

/** Port of BopTermsDownloadDialog — drives the async server-side export */
export const TermsDownloadDialog = ({ open, onOpenChange, onConfirm, isDownloading, versions, isVersionsLoading, versionsError, onRetryVersions }: TermsDownloadDialogProps) => {
  const [statuses, setStatuses] = useState<UserStatusValue[]>([...DEFAULT_DOWNLOAD_STATUSES]);
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);

  // State resets to defaults each time the dialog opens
  useEffect(() => {
    if (open) {
      setStatuses([...DEFAULT_DOWNLOAD_STATUSES]);
      setSelectedVersions([]);
    }
  }, [open]);

  const toggleStatus = (status: UserStatusValue, checked: boolean) => {
    setStatuses((previous) => (checked ? [...previous, status] : previous.filter((existing) => existing !== status)));
  };

  const toggleVersion = (version: string, checked: boolean) => {
    setSelectedVersions((previous) => (checked ? [...previous, version] : previous.filter((existing) => existing !== version)));
  };

  const hasNoStatus = statuses.length === 0;

  return (
    <Dialog onOpenChange={(next) => (isDownloading && !next ? undefined : onOpenChange(next))} open={open}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Download terms agreements</DialogTitle>
          <DialogDescription>Choose which user statuses and terms versions to include in the export.</DialogDescription>
        </DialogHeader>

        <fieldset>
          <legend className="text-sm font-medium">User statuses</legend>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {USER_STATUS_VALUES.map((status) => (
              <label className="flex items-center gap-2 text-sm" htmlFor={`terms-export-status-${status}`} key={status}>
                <Checkbox checked={statuses.includes(status)} id={`terms-export-status-${status}`} onCheckedChange={(checked) => toggleStatus(status, checked === true)} />
                {capitalize(status)}
              </label>
            ))}
          </div>
          {hasNoStatus ? <p className="mt-2 text-sm text-red-500">Select at least one status.</p> : null}
        </fieldset>

        <fieldset>
          <legend className="text-sm font-medium">Terms versions</legend>
          {isVersionsLoading ? (
            <div className="mt-2 flex flex-col gap-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-5 w-40" />
            </div>
          ) : versionsError ? (
            <div className="mt-2 flex items-center gap-3">
              <p className="text-sm text-red-500">{versionsError}</p>
              <Button onClick={onRetryVersions} size="sm" type="button" variant="outline">
                Retry
              </Button>
            </div>
          ) : (
            <div className="mt-2 flex max-h-48 flex-col gap-2 overflow-y-auto">
              {versions.length === 0 ? <p className="text-sm text-muted-foreground">No versions available.</p> : null}
              {versions.map((version) => {
                const note = getTermsVersionNote(version);
                return (
                  <label className="flex items-center gap-2 text-sm" htmlFor={`terms-export-version-${version}`} key={version}>
                    <Checkbox
                      checked={selectedVersions.includes(version)}
                      id={`terms-export-version-${version}`}
                      onCheckedChange={(checked) => toggleVersion(version, checked === true)}
                    />
                    {version}
                    {note ? <span className="text-xs text-muted-foreground">— {note}</span> : null}
                  </label>
                );
              })}
            </div>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            {selectedVersions.length === 0 ? 'All versions — leave empty to include every version.' : 'Leave empty to include every version.'}
          </p>
        </fieldset>

        <DialogFooter>
          <Button disabled={isDownloading} onClick={() => onOpenChange(false)} type="button" variant="outline">
            Cancel
          </Button>
          <Button className="gap-2" disabled={hasNoStatus || isDownloading} onClick={() => onConfirm({ statuses, versions: selectedVersions })} type="button" variant="primary">
            {isDownloading ? <Spinner className="size-4" /> : <Download className="size-4" />}
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
TermsDownloadDialog.displayName = 'TermsDownloadDialog';
